import { Link, MessageBar, MessageBarBody, Spinner, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import type { MouseEvent } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import type { AuditEvent } from '../api/types';
import { formatDateTime } from '../lib/format';
import { auditLabel, clockTime } from '../pages/overview/model';
import { vt } from '../theme';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', rowGap: '2px', minWidth: 0 },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', height: '24px' },
  title: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    '& th': {
      textAlign: 'start',
      fontWeight: tokens.fontWeightRegular,
      fontSize: '11px',
      letterSpacing: '0.3px',
      color: vt.fg3,
      height: '26px',
      padding: 0,
      borderBottom: `1px solid ${vt.surface2}`,
    },
    '& td': {
      // 32 px a row with its border, as in mockup 01.
      height: '31px',
      padding: '0 12px 0 0',
      lineHeight: '30px',
      borderBottom: `1px solid ${vt.surface2}`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& tbody tr:last-child td': { borderBottomStyle: 'none' },
  },
  time: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', color: vt.fg4 },
  actor: { color: vt.fg3 },
  event: { color: vt.fg1 },
  detail: { color: vt.fg3 },
  empty: { color: vt.fg3, paddingBlock: '12px' },
  fullLog: { color: vt.brand, fontSize: '11.5px' },
});

/** A Fluent link (brand colour, focus ring) that navigates inside the app like a router link. */
function FullLogLink({ to, className }: { to: string; className: string }) {
  const href = useHref(to);
  const navigate = useNavigate();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Let the browser handle new-tab and new-window clicks.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <Link href={href} onClick={onClick} className={className}>
      Full log
    </Link>
  );
}

export interface ActivityTableProps {
  events: AuditEvent[] | null;
  loading: boolean;
  error: string | null;
  /** Where "Full log" leads (the Activity page); no link when unset. */
  fullLogTo?: string;
  /** The "Activity" heading row; off where the page title already says it. */
  heading?: boolean;
}

/** The audit trail: Time, Actor, Event, Detail, newest first. Paging is the caller's. */
export function ActivityTable({ events, loading, error, fullLogTo, heading = true }: ActivityTableProps) {
  const s = useStyles();
  return (
    <section className={s.section} aria-label="Activity">
      {heading && (
        <div className={s.head}>
          <h2 className={s.title}>Activity</h2>
          {fullLogTo && <FullLogLink to={fullLogTo} className={s.fullLog} />}
        </div>
      )}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      <table className={s.table}>
        <colgroup>
          <col style={{ width: '84px' }} />
          <col style={{ width: '142px' }} />
          <col style={{ width: '242px' }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Actor</th>
            <th scope="col">Event</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {events?.map((e) => {
            const { event, detail } = auditLabel(e);
            return (
              <tr key={e.id}>
                <td className={s.time}>
                  <Tooltip content={formatDateTime(e.at)} relationship="description">
                    <span>{clockTime(e.at)}</span>
                  </Tooltip>
                </td>
                <td className={s.actor}>{e.actor}</td>
                <td className={s.event}>{event}</td>
                <td className={s.detail} title={detail}>
                  {detail}
                </td>
              </tr>
            );
          })}
          {events?.length === 0 && (
            <tr>
              <td colSpan={4} className={s.empty}>
                Nothing has happened yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {loading && <Spinner size="tiny" label="Loading activity" />}
    </section>
  );
}
